import { LatestImageInfo } from "./latest_image_info"
import { MaskInfo } from './MaskInfo'
import { fabric } from 'fabric'

import ImageJs from 'image-js'
import { names as GreyAlgorithm } from 'image-js/src/image/transform/greyAlgorithms';
import { GREY } from 'image-js/src/image/core/kindNames';

import '../../utils/ImageJsFilters'
import "../../utils/fabricjs-custom-filters/filter-flex-blend-image"
import { Size } from "@/shared/sync/mediaRepository/size";
import { CanvasRuntime, MediaImageRepositoryProcessing } from "../../../../shared/media_image_repository_processing";
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle";
import { HEIF_JPEG_QUALITY, HeifConverter } from "@/shared/sync/HeifConverter";
import { tryDecodeHeifInWorker } from "./heifWorkerClient";
import { looksLikeHeic } from "@/utils/supportedImageFormats";
import { fileToDataUrl } from "@/utils/uploadFiles";

class DomCanvasRuntime extends CanvasRuntime {
  create(width: number, height: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = width; c.height = height;
    return c;
  }
  get2D(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');
    return ctx;
  }
  async toBlobUrl(canvas: HTMLCanvasElement, mime: string): Promise<string> {
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), mime);
    });
    return URL.createObjectURL(blob);
  }
  async toDataURL(canvas: HTMLCanvasElement, mime: string = 'image/png'): Promise<string> {
    return canvas.toDataURL(mime);
  }
  toHTMLCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
    return canvas;
  }
}

export class MediaImageRepositoryProcessingDOM extends MediaImageRepositoryProcessing {
    private static pixijsFilterBackend: any;
    private static webglFilterBackend: any;
    private static canvasElement: HTMLCanvasElement;
    private static canvas: fabric.Canvas;

    constructor() { super(new DomCanvasRuntime()); }

        // fabricjs helpers

  private applyFabricJsFilter(fbImage: any, executeFilter: () => void): string {
    MediaImageRepositoryProcessingDOM.pixijsFilterBackend = fabric.filterBackend;
    if (MediaImageRepositoryProcessingDOM.webglFilterBackend && MediaImageRepositoryProcessingDOM.webglFilterBackend.gl.drawingBufferHeight != 0 && MediaImageRepositoryProcessingDOM.webglFilterBackend.gl.drawingBufferWidth != 0) {
      fabric.filterBackend = MediaImageRepositoryProcessingDOM.webglFilterBackend;
    } else {
      let prevGL = MediaImageRepositoryProcessingDOM.webglFilterBackend?.gl;
      // @ts-ignore
      MediaImageRepositoryProcessing.webglFilterBackend = fabric.initFilterBackend();
      fabric.filterBackend = MediaImageRepositoryProcessingDOM.webglFilterBackend;

      if (!prevGL) {
        MediaImageRepositoryProcessingDOM.canvasElement = document.createElement("canvas");
        MediaImageRepositoryProcessingDOM.canvas = new fabric.Canvas(MediaImageRepositoryProcessingDOM.canvasElement);
        
        // Register WebGL context event listeners only once when canvas is created
        MediaImageRepositoryProcessingDOM.canvasElement.addEventListener('webglcontextlost', function (event) {
          event.preventDefault();
          console.log('webglcontextlost');
        }, false);

        MediaImageRepositoryProcessingDOM.canvasElement.addEventListener('webglcontextrestored', function (event) {
          console.log('webglcontextrestored');
        }, false);
      }
    }

    executeFilter()

    MediaImageRepositoryProcessingDOM.webglFilterBackend = fabric.filterBackend;

    MediaImageRepositoryProcessingDOM.canvas.clear();
    MediaImageRepositoryProcessingDOM.canvas.add(fbImage);
    fbImage.applyFilters();
    MediaImageRepositoryProcessingDOM.canvas.renderAll();

    let filteredBase64 = MediaImageRepositoryProcessingDOM.canvas.toDataURL();

    fabric.filterBackend = MediaImageRepositoryProcessingDOM.pixijsFilterBackend;
    return filteredBase64
  }

  // imageJs helpers
  public async extractBoundingBox(base64Image: string, isMask: boolean, threshold: number | null = null, abortSignal?: AbortSignal): Promise<Rectangle> {
    let image = await ImageJs.load(base64Image);
    let boundingBox = this.doExtractBoundingBox(image, isMask, threshold, abortSignal);
    return boundingBox;
  }

  /**
   * Reads an uploaded file as a data URL, transcoding HEIC/HEIF to JPEG on the way.
   *
   * The counterpart to `convertToHeicToJpegIfNeeded` for callers that hold the File
   * itself: that one starts from a data URL, so a HEIC upload was base64'd whole,
   * fetched back into a Blob to sniff its magic bytes, then base64'd a second time
   * before libheif saw anything. Here the file's own bytes go to the decoder and
   * only the JPEG result is base64'd — for a folder of large HEICs, several passes
   * over every byte that now don't happen at all.
   *
   * A file that only *looks* HEIC (wrong extension, or a name the sniff would have
   * cleared) falls back to its own bytes rather than failing the import.
   */
  public async readUploadedImageAsDataUrl(file: File): Promise<string> {
    if (looksLikeHeic(file)) {
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const inWorker = await tryDecodeHeifInWorker(bytes, HEIF_JPEG_QUALITY);
        if (inWorker) return await fileToDataUrl(inWorker);
        return await HeifConverter.shared.convertBlob(file);
      } catch (error) {
        console.error(`readUploadedImageAsDataUrl: ${file.name} is not decodable HEIF`, error);
      }
    }
    return fileToDataUrl(file);
  }

  public async extractBoundingBoxWithSize(base64Image: string, isMask: boolean, threshold: number | null = null, abortSignal?: AbortSignal): Promise<{ rect: Rectangle; width: number; height: number }> {
    let image = await ImageJs.load(base64Image);
    let boundingBox = this.doExtractBoundingBox(image, isMask, threshold, abortSignal);
    return { rect: boundingBox, width: image.width, height: image.height };
  }

  private doExtractBoundingBox(image: ImageJs, isMask: boolean, threshold: number | null = null, abortSignal?: AbortSignal): Rectangle {
    // @ts-ignore
    let result = image.extractBoundingBoxFromChannel({
      shouldFlip: isMask, // our masks are flipped. white represents areas to remove and black areas to keep. with opacity of 4 channels it's the other way around.
      threshold: threshold != null ? threshold : this.maskThreshold,
      signal: abortSignal
    });
    let rectangle = new Rectangle(result.x, result.y, result.width, result.height)
    return rectangle
  }

  public async hasTransparency(base64Image: string, invert: boolean = false): Promise<boolean> {
    if (!base64Image) {
      return false;
    }
    let image = await ImageJs.load(base64Image)
    // @ts-ignore
    let hasTransparency = image.hasTransparency({ invert: invert })
    return hasTransparency
  }

  public async invertMask(base64Image: string): Promise<string> {
    let greyMask = await ImageJs.load(base64Image);
    let invertedMask = greyMask.invert()
    return invertedMask.toBase64();
  }

  public async extractGreyMaskFromColorImage(base64Image: string): Promise<HTMLImageElement> {
    let colorImage = await ImageJs.load(base64Image);
    let greyMask = colorImage.grey({ algorithm: GreyAlgorithm.AVERAGE, keepAlpha: false });
    let greyMaskBase64 = await greyMask.toBase64();
    let greyMaskDataUrl = 'data:image/png;base64,' + greyMaskBase64;
    let greyHtmlMask = await this.loadImage(greyMaskDataUrl);
    return greyHtmlMask;
  }

  public async extractAlphaMask(base64Image: string, shouldFlip: boolean = false): Promise<HTMLImageElement> {
    let greyMask = await ImageJs.load(base64Image);
    if (shouldFlip) {
      greyMask = greyMask.invert();
    }
    // @ts-ignore
    let alphaMask = greyMask.alphaFromGrey({});
    let alphaMaskBase64 = await alphaMask.toBase64();
    let alphaMaskDataUrl = 'data:image/png;base64,' + alphaMaskBase64;
    let alphaHtmlMask = await this.loadImage(alphaMaskDataUrl);
    return alphaHtmlMask;
  }

  public async extractMask(base64Image: string, threshold: number | null = null, invert: boolean = false, abortSignal?: AbortSignal): Promise<MaskInfo> {
    let image = await ImageJs.load(base64Image);
    let mask;
    if (image.channels == 4) {
      mask = image.getChannel(image.channels - 1);
    } else if(image.channels == 2) { 
      mask = image.getChannel(1);
    } else {
      mask = new ImageJs(image.width, image.height, new Uint8Array(image.width * image.height), {
        kind: GREY, // grayscale
        components: 1,
      });
      // @ts-ignore
      mask = mask.greyFromAlpha({ threshold: threshold != null ? threshold : this.maskThreshold, invert: invert, signal: abortSignal });
    }
    
    if (abortSignal?.aborted) {
      return;
    }
    if (!invert) {
      mask = mask.invert()
    }
    
    let croppedImage: ImageJs;
    try {
      // @ts-ignore
      let boundingBox = mask.extractBoundingBoxFromChannel({
        shouldFlip: true,
        threshold: threshold != null ? threshold : this.maskThreshold,
        component: 1,
        signal: abortSignal
      });

      croppedImage = mask.crop({
        x: boundingBox.left,
        y: boundingBox.top,
        width: boundingBox.width,
        height: boundingBox.height,
      });

    } catch {
      croppedImage = image;
    }
    let hasTransparency = croppedImage.width !== image.width || croppedImage.height !== image.height


    let maskBase64 = await mask.toBase64();
    let size = new Size(mask.width, mask.height);
    let blob = await this.base64ToBlobUrl(maskBase64)

    let maskInfo = {
      blob: blob,
      base64: 'data:image/png;base64,' + maskBase64,
      size: size,
      hasTransparency: hasTransparency
    };
    return maskInfo;
  }

  // some images are either jpeg or premultiplied and there's no specific alpha channel. 
  // if that's the case we add it.
  async normalizePngImage(originalBase64: string, abortSignal?: AbortSignal) {
    // let original = await ImageJs.load(originalBase64);
    // if (original.channels == 3)  { // some images are either jpeg or premultiplied and there's no specific alpha channel. 
    //   // if that's the case we add it.
    //   original = original.rgba8();  
    // }
    // let resultBase64 = 'data:image/png;base64,' + original.toBase64();
    // let blob = this.base64ToBlobUrl(resultBase64)
    // return blob;
    return originalBase64;
  }

  async generateLatestImageInfo(originalBase64: string,
    maskBase64: string,
    threshold: number | null = null,
    absolueBoundingBox?: Rectangle
  ): Promise<LatestImageInfo> {
    let original = await ImageJs.load(originalBase64);
    let rgbMask = await ImageJs.load(maskBase64);

    let mask = rgbMask.getChannel(0);
    // let invertedMask = mask.invert()
    let aspectFitMask = await this.doAspectFitMaskToImage(original, mask);

    // @ts-ignore
    let boundingBox = absolueBoundingBox ?? aspectFitMask.extractBoundingBoxFromChannel({
      shouldFlip: true,
      threshold: threshold != null ? threshold : this.maskThreshold, //255 - (threshold != null ? threshold : this.maskThreshold),
      component: 0 // red
    });
    boundingBox.width = Math.max(0, Math.min(boundingBox.width, aspectFitMask.width - boundingBox.x));
    boundingBox.height = Math.max(0, Math.min(boundingBox.height, aspectFitMask.height - boundingBox.y));

    let fittedImage = original.crop(boundingBox).rgba8()
    let fittedMask = aspectFitMask.crop(boundingBox);

    let latestImage = fittedImage.clone()
    latestImage.setChannel(3, fittedMask.invert())

    let relativeBoundingBox = new Rectangle(
      boundingBox.x / aspectFitMask.width,
      boundingBox.y / aspectFitMask.height,
      boundingBox.width / aspectFitMask.width,
      boundingBox.height / aspectFitMask.height
    );

    let result = new LatestImageInfo(
      latestImage,
      fittedImage,
      aspectFitMask,
      fittedMask,
      relativeBoundingBox
    );

    return result;
  }

  public async trim(base64Image: string, isMask: boolean = false, threshold: number | null = null, abortSignal?: AbortSignal): Promise<string> {
    let boundingBox = await this.extractBoundingBox(base64Image, isMask, threshold, abortSignal);
    let image = await ImageJs.load(base64Image);
    let croppedImage = image.crop(boundingBox)
    let base64 = croppedImage.toDataURL();
    return base64;
  }

  async postProcessFromBazaartBg(inputImage: string, ratioX: number, ratioY: number): Promise<string> {
    let buffer = this.base64ToUint8Array(inputImage);
    let rgbaImage = await ImageJs.load(buffer);
    let image = rgbaImage.getChannel(0);
    let resizedImage = image
      .resize({
        width: image.width * ratioX,
        height: image.height * ratioY,
      });
    //  let blob = await resizedImage.toBlob();
    let base64OutputImage = await resizedImage.toBase64();
    let blob = await this.base64ToBlobUrl(base64OutputImage)
    return blob;
  }

  async aspectFitMaskToImage(image: HTMLImageElement, mask: HTMLImageElement): Promise<HTMLImageElement> {
    let imageJs = await ImageJs.load(image.src);
    let maskJs = await ImageJs.load(mask.src);
    let aspectFitMaskJs = await this.doAspectFitMaskToImage(imageJs, maskJs);
    let aspectFitMask = await this.loadImage(aspectFitMaskJs.toDataURL());
    return aspectFitMask;
  }

  private async doAspectFitMaskToImage(image: ImageJs, mask: ImageJs): Promise<ImageJs> {
    let maskAR = mask.width / mask.height;
    let imageAR = image.width / image.height;
    let stretchRatio = 1.0;
    if (imageAR > maskAR) {
      stretchRatio = image.height / mask.height;
    } else {
      stretchRatio = image.width / mask.width;
    }
    let resizedMask = mask.getChannel(0).resize({
      factor: stretchRatio
    })
    let aspectFitMask = resizedMask.pad({
      // @ts-ignore
      size: [Math.floor((image.width - resizedMask.width) / 2), Math.floor((image.height - resizedMask.height) / 2)],
      algorithm: 'set',
      color: [255]
    })
    return aspectFitMask
  }

  public async applyImageJsMaskToImage(image: ImageJs, mask: ImageJs): Promise<ImageJs> {
    let base64Image = await this.loadImage(image.toDataURL())
    let base64Mask = await this.loadImage(mask.toDataURL())
    let base64Masked = await this.applyMaskToImage(base64Image, base64Mask)
    let masked = await ImageJs.load(base64Masked);
    return masked
  }

  // fabric-based resize (original behavior)
  async resize(image: HTMLImageElement, ratioX: number, ratioY: number, shouldPad: boolean = false): Promise<HTMLImageElement> {
    const fbImage = new fabric.Image(image, {
      centeredScaling: false,
      scaleX: ratioX,
      scaleY: ratioY,
      width: image.width,
      height: image.height
    });

    const canvasElement = document.createElement('canvas');
    if (shouldPad) {
      const maxEdge = Math.max(image.width * ratioX, image.height * ratioY);
      canvasElement.width = maxEdge;
      canvasElement.height = maxEdge;
      fbImage.originX = 'center';
      fbImage.originY = 'center';
      fbImage.top = maxEdge / 2;
      fbImage.left = maxEdge / 2;
    } else {
      canvasElement.width = image.width * ratioX;
      canvasElement.height = image.height * ratioY;
    }

    const canvas = new fabric.Canvas(canvasElement, { imageSmoothingEnabled: true, enableRetinaScaling: true });
    try {
      canvas.add(fbImage);
      canvas.renderAll();

      const resizedBase64Image = canvas.toDataURL();
      const resizedImage = await this.loadImage(resizedBase64Image);
      return resizedImage;
    } finally {
      canvas.dispose();
    }
  }

  async applyMaskToImage(image: HTMLImageElement, mask: HTMLImageElement, flip: boolean = true): Promise<string> {
      const fbImage = new fabric.Image(image);
    fbImage.dirty = true;
    fbImage.objectCaching = false;

    const fbMask = new fabric.Image(mask);
    fbMask.dirty = true;
    fbMask.objectCaching = false;

    const execute = () => {
      // @ts-ignore
      let maskFilter = new fabric.Image.filters.FlexBlendImage();
      maskFilter.image = fbMask;
      maskFilter.mode = 'mask';
      maskFilter.shouldFlip = flip;
      maskFilter.transparentMask = false;

      MediaImageRepositoryProcessingDOM.canvasElement.width = fbImage.width;
      MediaImageRepositoryProcessingDOM.canvasElement.height = fbImage.height;
      MediaImageRepositoryProcessingDOM.canvas.width = fbImage.width;
      MediaImageRepositoryProcessingDOM.canvas.height = fbImage.height;

      fbImage.filters.push(maskFilter);
    }

    let maskedImage = this.applyFabricJsFilter(fbImage, execute)
    return maskedImage;

  }
}